import logger from '@eneco/common/log';
import { SessionContextValue, UseSessionOptions, useSession as useNextAuthSession } from 'next-auth/react';

import { isNextAuthEnabled } from './isNextAuthEnabled';

export type UseSessionResult = SessionContextValue & {
  /** True while session is resolving or authenticated without an access token yet (e.g. DC fetches should wait). */
  isAuthPending: boolean;
};

const withAuthPending = (session: SessionContextValue): UseSessionResult => ({
  ...session,
  isAuthPending: session.status === 'loading' || (session.status === 'authenticated' && !session.data?.accessToken),
});

const fallbackSession: UseSessionResult = {
  data: null,
  status: 'unauthenticated',
  update: () => Promise.resolve(null),
  isAuthPending: false,
};

/** Wrapper around the original next-auth useSession that
 * provides a fallback session in case of an error. This way,
 * we can use useSession in containers that do not use Auth.js.
 * {@eneco/link https://next-auth.js.org/getting-started/client#usesession View original documentation}
 */
export const useSession = (options?: UseSessionOptions<boolean>): UseSessionResult => {
  try {
    const session = useNextAuthSession(options);
    return withAuthPending(session);
  } catch (error) {
    if (isNextAuthEnabled) {
      logger.error('5y6Z1G', 'Failed to fetch session', { Error: error });
    }
    return fallbackSession;
  }
};
